import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { eur, frDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, CreditCard, ShieldCheck, Loader2, Download, PenLine, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const REFUSAL = [
  { k: "prix", l: "Prix trop élevé" },
  { k: "date", l: "Date indisponible" },
  { k: "autre_presta", l: "Choix d'un autre prestataire" },
  { k: "autre", l: "Autre" },
];

export default function Portal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [refuseOpen, setRefuseOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [freeReason, setFreeReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/portal/${token}`).then(({ data }) => setData(data)).catch(() => setData("error"));
  useEffect(() => { load(); }, [token]);

  if (data === "error") return <Center>Document introuvable.</Center>;
  if (!data) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#FF6A00]" /></Center>;

  const q = data.quote;
  const org = data.org;
  const color = org.brand_color || "#FF6A00";
  const site = org.brand_website ? (org.brand_website.startsWith("http") ? org.brand_website : `https://${org.brand_website}`) : "";
  const sig = q.signature || null;
  const signNow = () => { if (sig?.link) window.open(sig.link, "_blank", "noopener"); };
  const verifySign = async () => {
    try {
      const { data: r } = await axios.get(`${API}/portal/${token}/signature/status`);
      if (r.signature?.status === "done") toast.success("Devis signé — merci !");
      else toast.info("Signature pas encore finalisée");
      await load();
    } catch { toast.error("Impossible de vérifier la signature"); }
  };
  const done = ["accepte", "facture", "paye"].includes(q.status);
  const refused = q.status === "refuse";
  const pb = data.photobooth || null;
  const approveDesign = async () => {
    try {
      await axios.post(`${API}/portal/${token}/photobooth/approve`);
      toast.success("Merci ! Design validé.");
      await load();
    } catch { toast.error("Impossible de valider le design"); }
  };

  const accept = async () => { setBusy(true); await axios.post(`${API}/portal/${token}/accept`); toast.success("Devis accepté !"); await load(); setBusy(false); };
  const refuse = async () => {
    const r = reason === "autre" ? freeReason : REFUSAL.find((x) => x.k === reason)?.l;
    if (!r) return toast.error("Sélectionnez un motif");
    await axios.post(`${API}/portal/${token}/refuse`, { reason: r });
    toast.success("Réponse envoyée");
    setRefuseOpen(false);
    await load();
  };
  const pay = async (which) => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/portal/${token}/checkout`, { which, origin_url: window.location.origin });
      window.location.href = res.checkout_url;
    } catch { toast.error("Erreur de paiement"); setBusy(false); }
  };
  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/portal/${token}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${q.is_invoice ? "facture" : "devis"}-${q.number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Erreur lors du téléchargement"); }
  };

  return (
    <div className="min-h-screen bg-zinc-100 py-8">
      <div className="mx-auto max-w-3xl px-4">
        <div className="rounded-t-2xl px-8 py-6 text-white" style={{ background: `linear-gradient(135deg, ${color}, ${color}dd)` }}>
          <div className="flex items-center gap-3">
            {org.logo ? (
              <div className="flex h-11 items-center justify-center rounded-xl bg-white/95 px-2"><img data-testid="portal-logo" src={org.logo} alt={org.name} className="max-h-9 max-w-32 object-contain" /></div>
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/25 text-xl font-bold">{(org.name || "E").slice(0, 1)}</div>
            )}
            <div><p className="text-xl font-bold">{org.name}</p><p className="text-sm text-white/80">{q.is_invoice ? "Facture" : "Proposition"} {q.number}</p>{org.brand_website && <p data-testid="portal-website" className="text-xs text-white/70">{org.brand_website}</p>}</div>
          </div>
        </div>

        <div className="rounded-b-2xl bg-white p-8 shadow-xl">
          {done && <Banner cls="bg-emerald-50 text-emerald-700"><Check className="h-5 w-5" /> Ce devis a été accepté. Merci !</Banner>}
          {refused && <Banner cls="bg-red-50 text-red-600"><X className="h-5 w-5" /> Ce devis a été décliné.</Banner>}

          <h1 className="mt-2 text-2xl font-bold text-gray-900">{q.title}</h1>
          {data.contact && <p className="text-gray-500">À l'attention de {data.contact.name}</p>}
          <Button data-testid="portal-download-pdf" onClick={downloadPdf} variant="outline" className="mt-3 rounded-full border-gray-300 text-gray-700 hover:bg-gray-50"><Download className="mr-1 h-4 w-4" />Télécharger le PDF</Button>

          <table className="mt-6 w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-gray-400"><th className="pb-2">Prestation</th><th className="pb-2 text-center">Qté</th><th className="pb-2 text-right">Total</th></tr></thead>
            <tbody>
              {q.lines.map((l, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-3"><p className="font-medium text-gray-900">{l.name}</p>{l.description && <p className="text-xs text-gray-400">{l.description}</p>}</td>
                  <td className="py-3 text-center text-gray-600">{l.qty}</td>
                  <td className="py-3 text-right text-gray-900">{eur(l.qty * l.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-4 ml-auto w-64 space-y-1 text-sm">
            <div className="flex justify-between text-gray-500"><span>Sous-total</span><span>{eur(q.subtotal)}</span></div>
            <div className="flex justify-between text-gray-500"><span>TVA</span><span>{eur(q.tax)}</span></div>
            <div className="flex justify-between border-t pt-1 text-lg font-bold text-gray-900"><span>Total</span><span>{eur(q.total)}</span></div>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-gray-50 p-4"><p className="text-xs text-gray-400">Acompte ({q.deposit_percent}%)</p><p className="text-lg font-bold text-gray-900">{eur(q.deposit)}</p>{q.deposit_paid && <span className="text-xs text-emerald-600">✓ Payé</span>}</div>
            <div className="rounded-xl bg-gray-50 p-4"><p className="text-xs text-gray-400">Solde</p><p className="text-lg font-bold text-gray-900">{eur(q.balance)}</p>{q.balance_paid && <span className="text-xs text-emerald-600">✓ Payé</span>}</div>
          </div>

          {pb && (
            <div data-testid="portal-photobooth" className="mt-6 rounded-xl border border-gray-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 font-semibold text-gray-800"><ImageIcon className="h-4 w-4" style={{ color }} />Design de votre photobooth</p>
                {pb.design_status === "approved"
                  ? <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600" data-testid="portal-pb-approved"><Check className="h-4 w-4" />Validé</span>
                  : null}
              </div>
              <img data-testid="portal-pb-image" src={pb.design_image} alt="Design photobooth" className="mt-3 max-h-[520px] w-full rounded-lg border border-gray-100 object-contain" />
              {pb.hashtag && <p className="mt-2 text-sm font-medium text-gray-600">{pb.hashtag}</p>}
              {pb.design_brief && <p className="mt-1 text-sm text-gray-500">{pb.design_brief}</p>}
              {pb.design_status !== "approved" && !refused && (
                <Button data-testid="portal-pb-approve" onClick={approveDesign} className="mt-3 rounded-full py-5 font-semibold text-white" style={{ background: color }}><Check className="mr-1 h-4 w-4" />Valider ce design</Button>
              )}
            </div>
          )}

          {org.cancellation_policy && (
            <div className="mt-6 flex gap-2 rounded-xl border border-gray-200 p-4 text-xs text-gray-500">
              <ShieldCheck className="h-4 w-4 flex-shrink-0 text-gray-400" />
              <div><p className="font-semibold text-gray-700">Politique d'annulation</p><p>{org.cancellation_policy}</p></div>
            </div>
          )}

          {sig && (
            <div data-testid="portal-signature" className="mt-6 rounded-xl border border-gray-200 p-4">
              {sig.status === "done" ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm font-semibold text-emerald-600"><Check className="h-4 w-4" />Devis signé électroniquement</span>
                  <a data-testid="portal-download-signed" href={`${API}/portal/${token}/signature/signed.pdf`} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline" style={{ color }}>Télécharger le devis signé</a>
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-sm text-gray-600"><PenLine className="h-4 w-4" />Signature électronique demandée</span>
                  <div className="flex gap-2">
                    <Button data-testid="portal-sign" onClick={signNow} className="rounded-full py-5 font-semibold text-white" style={{ background: color }}><PenLine className="mr-1 h-4 w-4" />Signer le devis</Button>
                    <Button data-testid="portal-verify-sign" onClick={verifySign} variant="outline" className="rounded-full border-gray-300 py-5 text-gray-700">J'ai signé</Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {!done && !refused && (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button data-testid="portal-accept" onClick={accept} disabled={busy} className="flex-1 rounded-full py-6 font-semibold text-white" style={{ background: color }}><Check className="mr-1 h-5 w-5" />Accepter le devis</Button>
              <Button data-testid="portal-refuse" onClick={() => setRefuseOpen(true)} variant="outline" className="flex-1 rounded-full border-gray-300 py-6 text-gray-700">Refuser</Button>
            </div>
          )}

          {done && (
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {!q.deposit_paid && q.deposit > 0 && <Button data-testid="portal-pay-deposit" onClick={() => pay("deposit")} disabled={busy} className="flex-1 rounded-full py-6 font-semibold text-white" style={{ background: color }}><CreditCard className="mr-1 h-5 w-5" />Payer l'acompte ({eur(q.deposit)})</Button>}
              {q.deposit_paid && !q.balance_paid && q.balance > 0 && <Button data-testid="portal-pay-balance" onClick={() => pay("balance")} disabled={busy} className="flex-1 rounded-full py-6 font-semibold text-white" style={{ background: color }}><CreditCard className="mr-1 h-5 w-5" />Payer le solde ({eur(q.balance)})</Button>}
              {q.deposit_paid && q.balance_paid && <p className="flex-1 text-center font-medium text-emerald-600">Tout est réglé — merci de votre confiance !</p>}
            </div>
          )}

          <p className="mt-8 text-center text-xs text-gray-400">{org.brand_website ? <a data-testid="portal-footer-website" href={site} target="_blank" rel="noreferrer" className="hover:underline" style={{ color }}>{org.brand_website}</a> : "Propulsé par HalgarOS"}</p>
        </div>
      </div>

      <Dialog open={refuseOpen} onOpenChange={setRefuseOpen}>
        <DialogContent className="border-zinc-800 bg-zinc-950">
          <DialogHeader><DialogTitle className="text-white">Motif du refus</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {REFUSAL.map((r) => (
              <button key={r.k} data-testid={`refuse-${r.k}`} onClick={() => setReason(r.k)} className={`block w-full rounded-xl border p-3 text-left text-sm ${reason === r.k ? "border-[#FF6A00] bg-[#FF6A00]/10 text-white" : "border-zinc-800 text-zinc-400"}`}>{r.l}</button>
            ))}
            {reason === "autre" && <Textarea className="border-zinc-800 bg-zinc-900" placeholder="Précisez…" value={freeReason} onChange={(e) => setFreeReason(e.target.value)} />}
            <Button data-testid="confirm-refuse" onClick={refuse} className="w-full rounded-full bg-red-600 font-semibold text-white hover:bg-red-500">Envoyer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Center = ({ children }) => <div className="flex min-h-screen items-center justify-center bg-zinc-100 text-gray-500">{children}</div>;
const Banner = ({ cls, children }) => <div className={`mb-4 flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium ${cls}`}>{children}</div>;
